import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { API } from "@/lib/api";

const PresenceCtx = createContext({
  online: [],
  selfConnectionId: null,
  connected: false,
  viewersForProject: () => [],
});

const ROUTE_LABELS = [
  [/^\/dashboard/, "Viewing Dashboard"],
  [/^\/my-tasks/, "Reviewing My Tasks"],
  [/^\/chat/, "In project chat"],
  [/^\/workspace\/[^/]+\/board/, "Viewing Kanban board"],
  [/^\/workspace\/[^/]+\/raid/, "Editing RAID register"],
  [/^\/workspace\/[^/]+\/meetings/, "In meeting hub"],
  [/^\/workspace\/[^/]+\/sprints/, "Planning sprint"],
  [/^\/workspace\/[^/]+\/dependencies/, "Mapping dependencies"],
  [/^\/workspace\/[^/]+\/gantt/, "Viewing Gantt"],
  [/^\/workspace\/[^/]+\/wiki/, "In project wiki"],
  [/^\/workspace\/[^/]+\/report/, "Reading status report"],
  [/^\/workspace\/[^/]+$/, "Viewing project"],
  [/^\/workspace$/, "Browsing workspace"],
  [/^\/portfolio/, "Reviewing portfolio"],
  [/^\/burndown/, "Reading burndown"],
  [/^\/reporting/, "Reading executive report"],
  [/^\/templates/, "Browsing templates"],
  [/^\/marketplace/, "Browsing marketplace"],
  [/^\/deals/, "Working pipeline"],
  [/^\/invoices\/aging/, "Reviewing aging"],
  [/^\/invoices/, "Reviewing invoices"],
  [/^\/compliance/, "In compliance centre"],
  [/^\/governance/, "Reading governance"],
  [/^\/contacts/, "In CRM contacts"],
  [/^\/team/, "Managing team"],
  [/^\/integrations/, "Configuring integrations"],
  [/^\/assistant/, "Chatting with AI"],
  [/^\/billing/, "Reviewing billing"],
  [/^\/pricing/, "Browsing pricing"],
];

const labelForPath = (pathname) => {
  for (const [re, label] of ROUTE_LABELS) {
    if (re.test(pathname)) return label;
  }
  return "Active";
};

const projectIdFromPath = (pathname) => {
  const m = pathname.match(/^\/workspace\/([^/]+)/);
  return m && m[1] !== "" ? m[1] : null;
};

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export const PresenceProvider = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [online, setOnline] = useState([]);
  const [selfConnectionId, setSelfConnectionId] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectDelayRef = useRef(RECONNECT_BASE_MS);
  // Keep latest pathname accessible inside stable callbacks without re-running the effect.
  const pathnameRef = useRef(pathname);
  useEffect(() => { pathnameRef.current = pathname; }, [pathname]);

  // Stable upsert helpers — referenced by message handler.
  const upsertUser = useCallback((u) => {
    setOnline((prev) => {
      const idx = prev.findIndex((p) => p.user_id === u.user_id);
      if (idx === -1) return [...prev, u];
      const next = prev.slice();
      next[idx] = { ...next[idx], ...u };
      return next;
    });
  }, []);

  const removeUser = useCallback((userId) => {
    setOnline((prev) => prev.filter((p) => p.user_id !== userId));
  }, []);

  // Open (or re-open) the WebSocket. Called on mount and after each disconnect.
  const connect = useCallback(() => {
    if (!user?.user_id) return;
    const wsUrl = API.replace(/^http/, "ws") + "/ws/presence";
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectDelayRef.current = RECONNECT_BASE_MS; // reset backoff on successful connect
      try {
        ws.send(JSON.stringify({
          type: "track_activity",
          context: labelForPath(pathnameRef.current),
          project_id: projectIdFromPath(pathnameRef.current),
        }));
      } catch { /* socket may have closed mid-flight */ }
      pingTimerRef.current = setInterval(() => {
        try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* ignored */ }
      }, 25_000);
    };

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        switch (data.type) {
          case "snapshot":
            setSelfConnectionId(data.self_connection_id);
            setOnline(data.users || []);
            break;
          case "user_went_online":
            if (data.user) upsertUser(data.user);
            break;
          case "user_went_offline":
            if (data.user_id) removeUser(data.user_id);
            break;
          case "user_activity_updated":
            if (data.user) upsertUser(data.user);
            break;
          default:
            break;
        }
      } catch (e) {
        console.warn("presence: bad frame", e);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      setOnline([]);
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      // Schedule reconnect with exponential backoff, capped at RECONNECT_MAX_MS.
      reconnectTimerRef.current = setTimeout(() => {
        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, RECONNECT_MAX_MS);
        connect();
      }, reconnectDelayRef.current);
    };

    ws.onerror = (e) => {
      console.warn("presence: ws error", e);
      // onclose fires after onerror — reconnect logic lives there.
    };
  }, [user?.user_id, upsertUser, removeUser]);

  useEffect(() => {
    if (!user?.user_id) return undefined;
    connect();
    return () => {
      // Prevent any pending reconnect from firing after unmount.
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      try { wsRef.current?.close(); } catch { /* already closed */ }
      wsRef.current = null;
    };
  }, [user?.user_id, connect]);

  // Push activity updates whenever the route changes.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({
        type: "track_activity",
        context: labelForPath(pathname),
        project_id: projectIdFromPath(pathname),
      }));
    } catch { /* socket churning */ }
  }, [pathname]);

  const viewersForProject = useCallback((projectId) => {
    if (!projectId) return [];
    return online.filter((u) => u.active_project_id === projectId);
  }, [online]);

  const value = useMemo(() => ({
    online,
    selfConnectionId,
    connected,
    viewersForProject,
  }), [online, selfConnectionId, connected, viewersForProject]);

  return <PresenceCtx.Provider value={value}>{children}</PresenceCtx.Provider>;
};

export const usePresence = () => useContext(PresenceCtx);
